import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Users, Radio, Calendar, ChevronRight, LogOut } from "lucide-react";
import { toast } from "sonner";

/**
 * Node-simplified landing for users in one or more groups.
 * If they have a single active group → redirect to it directly.
 * Multiple → present a list.
 */
export default function GroupPicker() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/groups");
      const list = r.data || [];
      setGroups(list);
      if (list.length === 1) {
        navigate(`/groups/${list[0].group_id}`, { replace: true });
      }
    } catch {
      // ignore
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center"><div className="label-eyebrow">Loading…</div></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-28">
      <header className="px-6 pt-10 pb-6 flex items-start justify-between">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Users size={12}/> Your groups</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Group</h1>
          <p className="text-sm text-zinc-400 mt-1">Tap a group to view its live map.</p>
        </div>
        <button
          onClick={logout}
          data-testid="node-logout"
          className="size-10 rounded-full border border-white/10 grid place-items-center hover:border-[#FF3B30]/40 hover:text-[#FF3B30]"
          title="Sign out"
        >
          <LogOut size={16}/>
        </button>
      </header>

      <section className="px-6">
        {groups.length === 0 ? (
          <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center">
            <div className="size-12 rounded-full bg-zinc-800 mx-auto grid place-items-center">
              <Users size={20} className="text-zinc-500"/>
            </div>
            <div className="font-display font-bold text-lg mt-3">No active groups</div>
            <div className="text-xs text-zinc-500 mt-1">You'll see groups here once they go live.</div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {groups.map((g) => (
              <button
                key={g.group_id}
                data-testid={`pick-group-${g.group_id}`}
                onClick={() => navigate(`/groups/${g.group_id}`)}
                className="w-full p-4 rounded-2xl border border-[#34C759]/30 bg-[#34C759]/5 hover:bg-[#34C759]/10 flex items-center gap-3 text-left"
              >
                <div className="size-12 rounded-full bg-[#34C759]/15 grid place-items-center">
                  <Users size={20} className="text-[#34C759]"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold truncate">{g.name}</div>
                  <div className="text-xs text-zinc-400 flex items-center gap-2">
                    {g.is_active && (
                      <span className="inline-flex items-center gap-1 text-[#34C759]">
                        <span className="size-1.5 rounded-full bg-[#34C759] animate-pulse"/> Live
                      </span>
                    )}
                    <span>{g.member_count} {g.member_count === 1 ? "device" : "devices"}</span>
                  </div>
                </div>
                <ChevronRight size={18} className="text-zinc-400"/>
              </button>
            ))}
          </div>
        )}
      </section>

      <footer className="px-6 mt-10 text-[11px] text-zinc-600 text-center">
        Connected as <span className="text-zinc-400">{user?.name}</span> · Managed by your plan owner.
      </footer>
    </div>
  );
}
